// New Patient modal — create patient with full field-level validation.

import { useState } from "react";
import type { JSX, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../lib/api.js";
import { useAuth } from "../hooks/useAuth.js";
import { Modal } from "./Modal.js";
import { validatePatientForm } from "../lib/patient-validation.js";
import { PersonalSection, AddressSection, MedicalDetailsSection, type PatientForm, INIT } from "./NewPatientModal.sections.js";
import { ClinicalSection } from "./NewPatientModal.clinical-section.js";

type F = PatientForm;
type Errors = ReturnType<typeof validatePatientForm>;

function handleApiError(
  err: unknown,
  setDuplicateId: (id: string | null) => void,
  setServerError: (msg: string | null) => void,
): void {
  if (!(err instanceof ApiError)) { setServerError("Connection error — please try again"); return; }
  const body = err.body as { type?: string; existing_patient_id?: string; error?: { fieldErrors?: Record<string, string[]> } } | null;
  if (err.status === 409 && body?.type === "duplicate_phone" && body.existing_patient_id) {
    setDuplicateId(body.existing_patient_id);
  } else {
    const fields = body?.error?.fieldErrors;
    setServerError(fields ? `Server rejected: ${Object.entries(fields).map(([k, v]) => `${k}: ${v.join(", ")}`).join(" | ")}` : `Server error ${err.status} — please try again`);
  }
}

async function submitPatient(form: F, coordinatorId: string): Promise<void> {
  await apiFetch<unknown>("/patients", {
    method: "POST",
    body: JSON.stringify({
      ...form,
      age:                          Number(form.age),
      chronic_conditions:           form.chronic_conditions,
      assigned_coordinator_id:      coordinatorId,
      timezone:                     "Asia/Kolkata",
      baseline_bp_systolic:         form.baseline_bp_systolic   ? Number(form.baseline_bp_systolic)   : undefined,
      baseline_bp_diastolic:        form.baseline_bp_diastolic  ? Number(form.baseline_bp_diastolic)  : undefined,
      baseline_heart_rate:          form.baseline_heart_rate    ? Number(form.baseline_heart_rate)    : undefined,
      baseline_blood_glucose:       form.baseline_blood_glucose ? Number(form.baseline_blood_glucose) : undefined,
      weight_kg:                    form.weight_kg              ? Number(form.weight_kg)              : undefined,
      recent_hospitalisation_reason: form.recent_hospitalisation_reason || undefined,
    }),
  });
}

interface Props { onClose: () => void; onCreated: () => void; }

export function NewPatientModal({ onClose, onCreated }: Props): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<F>(INIT);
  const [fieldErrors, setFieldErrors] = useState<Errors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const update = (patch: Partial<F>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
    setFieldErrors((prev) => ({ ...prev })); // keep showing existing errors until resubmit
  };

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const errs = validatePatientForm(form);
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setSaving(true); setServerError(null); setDuplicateId(null); setFieldErrors({});
    try {
      await submitPatient(form, user?.id ?? "");
      onCreated();
    } catch (err) {
      handleApiError(err, setDuplicateId, setServerError);
    } finally { setSaving(false); }
  }

  return (
    <Modal title="New Patient" onClose={onClose}>
      <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-1">
        <PersonalSection      f={form} on={update} e={fieldErrors} />
        <AddressSection       f={form} on={update} e={fieldErrors} />
        <ClinicalSection      f={form} on={update} e={fieldErrors} />
        <MedicalDetailsSection f={form} on={update} />
        {serverError && <p role="alert" className="text-sm text-red-600 pt-2 border-t border-red-100">{serverError}</p>}
        {duplicateId && (
          <div role="alert" className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-2">
            <p className="text-sm text-amber-800 font-medium">A patient with this phone number already exists.</p>
            <button
              type="button"
              onClick={() => { onClose(); navigate(`/patients/${duplicateId}`); }}
              className="text-sm text-navy-800 underline mt-1"
            >
              View existing patient record →
            </button>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="bg-navy-700 text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-navy-800 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : "Create patient"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
